import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import PageHeader from '@features/PageHeader';
import { ModalConfirm } from '@components/Modal';
import Button from '@components/Button';
import { useTranslation, useLocations } from '@hooks';
import { alertActions, knowledgeBaseActions } from '@actions';
import { knowledgeBaseService } from '@services';
import { ROLE } from '@constants/user';
import { ChatbotHeaderIcon } from 'assets/images/icons';
import EditModal, { FORM_TYPE } from './EditModal';
import KnowledgeBaseTable from './KnowledgeBaseTable';
import FiltersPanel from './FiltersPanel';

const Container = styled('div')({
    '@media (max-width: 1024px)': {
        padding: 16,
        height: 'calc(var(--100vh) - 108px)',
        overflowY: 'auto',
    },
});

const StyledFiltersPanel = styled(FiltersPanel)({
    '& svg': {
        height: 'auto',
        width: 'auto',
    },
});

const KnowledgeBase = () => {
    const { t } = useTranslation();

    const [selectedContent, setSelectedContent] = useState(null);
    const [modal, setModal] = useState({ open: false, type: FORM_TYPE.NEW });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filter, setFilter] = useState({
        locations: [],
    });
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

    const user = useSelector(state => state.auth.user);
    const brands = useSelector(state => state.brands.data);
    const locations = useSelector(state => state.account?.account?.companies || []);
    const dispatch = useDispatch();
    const { mapLocationsToFormValue } = useLocations();
    const firstRender = useRef(true);

    useEffect(() => {
        if (locations.length) {
            setFilter(prev => ({ ...prev, locations: mapLocationsToFormValue() }));
        }
    }, [locations]);

    const currentQuery = useMemo(() => {
        return { ...filter, locations: filter.locations.map(l => l.id) };
    }, [filter]);

    useEffect(() => {
        if (firstRender.current && currentQuery.locations.length) {
            dispatch(knowledgeBaseActions.get(currentQuery, page, rowsPerPage));
            firstRender.current = false;
        }
    }, [dispatch, currentQuery]);

    const onPageChange = (_, newPage) => {
        setPage(newPage);
        dispatch(knowledgeBaseActions.get(currentQuery, newPage, rowsPerPage));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        dispatch(knowledgeBaseActions.get(currentQuery, 0, parseInt(event.target.value, 10)));
    };

    const onDelete = async () => {
        try {
            await knowledgeBaseService.deleteKnowledgeBase(selectedContent.id);
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            if (page === 0) {
                await dispatch(knowledgeBaseActions.get(currentQuery, page, rowsPerPage));
            } else {
                setPage(0); // INFO:Triggers refetch of templates
            }
            setDeleteModalOpen(false);
            setSelectedContent(null);
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.deleteFail')));
        }
    };

    const onAddContentClick = () => setModal({ open: true, type: FORM_TYPE.NEW });

    const onApplyFilters = () => {
        dispatch(knowledgeBaseActions.get(currentQuery, 0, rowsPerPage));
        setPage(0);
    };

    return (
        <Container>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<ChatbotHeaderIcon />}
                title={t('KnowledgeBase.title')}
                subtitle={t('KnowledgeBase.subtitle')}
            >
                {user?.role !== ROLE.USER && (
                    <Button variant="contained" onClick={onAddContentClick}>
                        {t('buttons.addContent')}
                    </Button>
                )}
                <StyledFiltersPanel
                    onApplyFilters={onApplyFilters}
                    filter={filter}
                    setFilter={setFilter}
                    withChips={false}
                    type="knowledge-base"
                />
            </PageHeader>
            <KnowledgeBaseTable
                setModal={setModal}
                setSelectedContent={setSelectedContent}
                page={page}
                onPageChange={onPageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={onRowsPerPageChange}
                setDeleteModalOpen={setDeleteModalOpen}
                locations={locations}
                brands={brands}
            />
            {modal.open ? (
                <EditModal
                    selectedContent={selectedContent}
                    setSelectedContent={setSelectedContent}
                    currentQuery={currentQuery}
                    modal={modal}
                    brands={brands}
                    setModal={setModal}
                    setPage={setPage}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    setDeleteModalOpen={setDeleteModalOpen}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </Container>
    );
};

export default KnowledgeBase;
